import { paletteFor, topicColorResolved } from "@app/core";
import { Card, cn } from "@app/ui";
import { useState } from "react";

import { washStyle } from "../../../features/block-wash.js";
import { useBlockStyle } from "../../../features/useBlockStyle.js";
import { formatDurationCompact } from "../../../lib/format.js";
import { useI18n } from "../../../lib/useI18n.js";
import { useTimeFormat } from "../../../lib/useTimeFormat.js";
import { useThemeStore, useTopicsStore } from "../../../stores/index.js";
import { ThemePreview } from "./ThemePreview.js";

const DAY_START_HOUR = 8;
const ITEM_DURATION_MIN = 60;
const MAX_DAY_ITEMS = 10;

/** Mini calendar-day mockup: one block per topic (alternating event/task,
 *  capped at 10) so every topic color shows in calendar context. Falls back to
 *  palette samples when no topics exist yet. Blocks mirror the REAL calendar
 *  block (`TaskBlock`/`TaskBlockContent`): same `task-wash` fill, rounded/
 *  border/shadow, title (`text-xs font-medium leading-tight`), and a time line
 *  in the same `font-mono` + `formatDurationCompact` format — just a touch
 *  more padding so the mockup reads clearly at sidebar width. */
function DayMockup() {
  const { locale, t } = useI18n();
  const config = useThemeStore((s) => s.config);
  const topics = useTopicsStore((s) => s.topics);
  const { tint, gradient } = useBlockStyle();
  const tf = useTimeFormat();

  const items = topics.slice(0, MAX_DAY_ITEMS).map((topic, i) => ({
    title: topic.name,
    color: topicColorResolved(config, topic.id, i),
  }));
  if (items.length === 0) {
    const palette = paletteFor(config);
    ["Standup", "Write report", "Design review"].forEach((title, i) => {
      items.push({ title, color: palette[(i * 3) % Math.max(palette.length, 1)] ?? "#888888" });
    });
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-lg border bg-card text-card-foreground shadow-sm">
      <div className="flex shrink-0 items-center justify-between border-b px-3 py-2">
        <span className="text-sm font-medium">
          {new Date().toLocaleDateString(locale, { weekday: "long", month: "short", day: "numeric" })}
        </span>
        <span className="rounded-md bg-primary px-2 py-0.5 text-xs font-medium text-primary-foreground">{t("common.today")}</span>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        {items.map((item, i) => {
          const { vars } = washStyle(
            { priority: "none", custom_color: null, is_event: i % 2 === 0 },
            item.color,
            tint,
            gradient,
          );
          const start = new Date();
          start.setHours(DAY_START_HOUR + i, 0, 0, 0);
          const end = new Date(start.getTime() + ITEM_DURATION_MIN * 60_000);
          const timeText = `${tf.hmDate(start)} – ${tf.hmDate(end)} (${formatDurationCompact(ITEM_DURATION_MIN)})`;
          return (
            <div key={i} className="flex items-center gap-1.5">
              <span className="w-10 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                {`${DAY_START_HOUR + i}:00`}
              </span>
              <div className="task-wash mb-2 min-w-0 flex-1 rounded border px-1.5 py-1.5 shadow-md" style={vars}>
                <span className="block truncate text-xs font-medium leading-tight">{item.title}</span>
                <span className="block truncate font-mono text-[calc(10px*var(--app-text-scale))] leading-tight opacity-80">
                  {timeText}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** The live preview beside the color board: toggles between a calendar-day
 *  mockup (default — one block per topic) and the compact theme overview.
 *  `height` (px) lets the host size it to match the color board's own
 *  rendered height (see `PaletteBoardWithPreview`); the day mockup scrolls
 *  internally if its blocks don't all fit. */
export function ThemePreviewPanel({ height }: { height?: number | undefined } = {}) {
  const { t } = useI18n();
  const [view, setView] = useState<"day" | "overview">("day");

  return (
    <Card className="flex min-h-80 flex-col p-3" style={height ? { height } : undefined}>
      <div className="mb-2 flex shrink-0 items-center justify-between gap-2">
        <h3 className="text-sm font-medium">{t("theme.preview")}</h3>
        <div className="flex gap-0.5 rounded-md border p-0.5">
          {(["day", "overview"] as const).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setView(v)}
              className={cn(
                "rounded px-2 py-0.5 text-xs",
                view === v ? "bg-accent font-medium" : "text-muted-foreground hover:text-foreground",
              )}
            >
              {t(v === "day" ? "theme.preview.day" : "theme.preview.overview")}
            </button>
          ))}
        </div>
      </div>
      <div className="min-h-0 flex-1">
        {view === "day" ? <DayMockup /> : <ThemePreview />}
      </div>
    </Card>
  );
}
